import { Box, Icon, Typography } from "@outschool/backpack";
import { Search as SearchFilters } from "@outschool/business-rules";
import { SearchFilters as SearchFiltersType } from "@outschool/gql-backend-generated";
import { SearchListingsQueryVariables } from "@outschool/gql-frontend-generated";
import { faArrowUpArrowDown } from "@outschool/icons";
import { useTranslation } from "@outschool/localization";
import { useSession } from "@outschool/ui-auth";
import { useSearchFiltersString } from "@outschool/ui-components-website";
import {
  ToggleButton,
  useImpressionTracking
} from "@outschool/ui-legacy-component-library";
import { useSelect } from "downshift";
import React from "react";

import { FEATURE_FLAGS } from "../../../../shared/Env";

export const CategorySortBy = ({
  updateListings,
  searchFilters,
  isMobile
}: {
  updateListings: (payload: SearchFiltersType) => void;
  searchFilters: SearchFiltersType;
  isMobile: boolean;
}) => {
  const { t } = useTranslation(
    "client\\components\\categories\\CategorySortBy"
  );
  return (
    <Box
      flex
      sx={{
        alignItems: "flex-start",
        justifyContent: "flex-end",
        flexGrow: 1
      }}
    >
      <Box flex sx={{ alignItems: "center", gap: "8px" }}>
        {!isMobile ? (
          <Typography
            variant="body2"
            sx={{ fontWeight: 500, whiteSpace: "nowrap", paddingLeft: "20px" }}
          >
            {t`Sort by`}
          </Typography>
        ) : null}
        <SortBy
          filters={searchFilters}
          setFilters={(el, filters) => {
            updateListings(filters);
          }}
          iconOnly={isMobile}
        />
      </Box>
    </Box>
  );
};

type SearchFilters = SearchListingsQueryVariables["searchFilters"];

interface SortByProps {
  filters: SearchFilters;
  setFilters: (key: string, filters: SearchFilters) => void;
  iconOnly?: boolean;
}

function SortBy({ filters, setFilters, iconOnly = false }: SortByProps) {
  const [node, setNode] = React.useState(null);
  const { t } = useTranslation(
    "client\\components\\categories\\CategorySortBy"
  );
  const { ORDER_OPTIONS, PROMOTED_ORDER_OPTIONS } = useSearchFiltersString();
  const { trackTouch: trackSortChange } = useImpressionTracking({
    node,
    trackingLabel: "sortOrderChanged",
    uniqueId: "sortOrderChanged"
  });

  const isGroupOnlySearch =
    filters?.delivery?.split(",")?.length === 1 &&
    filters?.delivery === "Group";

  const { currentUser } = useSession();

  const promotedRankingsEnabled: boolean =
    FEATURE_FLAGS.PROMOTED_SEARCH_LOGGING as boolean;

  const orderOptions = promotedRankingsEnabled
    ? PROMOTED_ORDER_OPTIONS
    : ORDER_OPTIONS;
  const options = isGroupOnlySearch
    ? orderOptions.filter(option => option.value !== "upcoming")
    : orderOptions;
  let defaultOrder = promotedRankingsEnabled
    ? "relevance"
    : currentUser
    ? "upcoming"
    : "popular";
  const defaultLabel = options.filter(option => option.value == defaultOrder)[0]
    .label;

  const {
    isOpen,
    selectedItem,
    getToggleButtonProps,
    getMenuProps,
    getItemProps
  } = useSelect({
    items: options,
    onSelectedItemChange: ({ selectedItem }) => {
      // @ts-ignore TS(2533): Object is possibly 'null' or 'undefined'.
      const order = selectedItem.value;
      trackSortChange({
        orderAfter: order,
        orderBefore: filters.order
      });
      setFilters(t("Order"), { order });
    }
  });

  return (
    <Box sx={{ position: "relative" }}>
      <ToggleButton
        sx={{
          padding: "1.1rem",
          "&:hover:not(:disabled)": {
            color: "white",
            backgroundColor: "peacock300"
          },
          "& > svg": {
            paddingTop: 0
          }
        }}
        {...getToggleButtonProps()}
      >
        <Icon
          icon={faArrowUpArrowDown}
          sx={{
            size: iconOnly ? "16px" : "1em",
            marginRight: iconOnly ? "0px" : "10px"
          }}
        />
        {!iconOnly && (selectedItem ? selectedItem.label : defaultLabel)}
      </ToggleButton>
      <ul
        {...getMenuProps({
          style: {
            backgroundColor: "white",
            borderRadius: 8,
            boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.25)",
            cursor: "pointer",
            display: isOpen ? "block" : "none",
            listStyleType: "none",
            marginTop: "5px",
            padding: "8px",
            position: "absolute",
            right: 0,
            textAlign: "left",
            zIndex: 2
          },
          // @ts-ignore TS(2322): Type 'Dispatch<SetStateAction<null>>' is not assig... Remove this comment to see the full error message
          ref: setNode
        })}
      >
        {isOpen &&
          options.map((option, index) => (
            <li
              key={index}
              style={{
                padding: "16px",
                backgroundColor:
                  (selectedItem ? selectedItem.label : defaultLabel) ===
                  option.label
                    ? "#E6E9FF"
                    : "initial",
                borderBottom:
                  index + 1 !== options.length
                    ? "1px solid rgba(221, 223, 227, 1)"
                    : "initial"
              }}
              {...(getItemProps({ item: option, index }) as object)}
            >
              <Typography variant="body1">{option.label}</Typography>
            </li>
          ))}
      </ul>
    </Box>
  );
}
